import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { InputMask } from '@/components/ui/InputMask';
import { Label } from '@/components/ui/Label';
import { alunoService, cursoService, cidadeService } from '@/services/api';
import { CreateAlunoDTO, UpdateAlunoDTO, Aluno, Cidade, Curso } from '@/types';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';
import { SkeletonForm } from '@/components/ui/SkeletonLoader';
import { ServerSearchSelect } from '@/components/ui/ServerSearchSelect';

const alunoSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  rg: z.string().optional(),
  cpf: z.string().min(11, 'CPF é obrigatório'),
  dataNascimento: z.string().min(1, 'Data de nascimento é obrigatória'),
  telefone: z.string().optional(),
  telefonePai: z.string().optional(),
  email: z.string().email('Email inválido').optional(),
  endereco: z.string().optional(),
  bairro: z.string().optional(),
  cep: z.string().optional(),
  cidadeId: z.number().min(1, 'Cidade é obrigatória'),
  cursoId: z.number().min(1, 'Curso é obrigatório'),
  rm: z.string().optional(),
  periodo: z.string().optional(),
  serie: z.string().optional(),
});

type AlunoFormData = z.infer<typeof alunoSchema>;

export const NovoAlunoPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateAlunos } = useQueryInvalidation();

  const [cpfValue, setCpfValue] = useState('');
  const [telefoneValue, setTelefoneValue] = useState('');
  const [telefonePaiValue, setTelefonePaiValue] = useState('');
  const [cepValue, setCepValue] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<AlunoFormData>({
    resolver: zodResolver(alunoSchema),
  });

  // Carregar dados do aluno se estiver editando
  const { data: alunoData, isLoading: isLoadingAluno, error: alunoError } = useQuery<Aluno>({
    queryKey: ['aluno', id],
    queryFn: () => alunoService.getById(Number(id)),
    enabled: isEditing,
  });

  useEffect(() => {
    if (alunoError) {
      const message = handleError(alunoError as any, 'Erro ao carregar aluno');
      setLoadError(message);
    }
  }, [alunoError, handleError]);

  useEffect(() => {
    if (!alunoData) return;

    setLoadError(null);
    const cidadeId = alunoData.cidadeId ?? alunoData.cidade?.id ?? 0;
    const cursoId = alunoData.cursoId ?? alunoData.curso?.id ?? 0;

    let dataFormatada = '';
    if (alunoData.dataNascimento) {
      const date = new Date(alunoData.dataNascimento);
      dataFormatada = date.toISOString().split('T')[0];
    }

    reset({
      nome: alunoData.nome,
      rg: alunoData.rg || '',
      cpf: alunoData.cpf || '',
      dataNascimento: dataFormatada,
      telefone: alunoData.telefone || '',
      telefonePai: alunoData.telefonePai || '',
      email: alunoData.email || '',
      endereco: alunoData.endereco || '',
      bairro: alunoData.bairro || '',
      cep: alunoData.cep || '',
      cidadeId,
      cursoId,
      serie: alunoData.serie || '',
      rm: alunoData.rm || '',
      periodo: alunoData.periodo || '',
    });
    setCpfValue(alunoData.cpf || '');
    setTelefoneValue(alunoData.telefone || '');
    setTelefonePaiValue(alunoData.telefonePai || '');
    setCepValue(alunoData.cep || '');
  }, [alunoData, reset]);

  const createMutation = useMutation({
    mutationFn: (data: CreateAlunoDTO) => alunoService.create(data),
    onSuccess: () => {
      invalidateAlunos();
      handleSuccess('Aluno criado com sucesso!');
      navigate('/alunos');
    },
    onError: (error: Error) => {
      handleError(error, 'Erro ao criar aluno');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAlunoDTO) => alunoService.update(Number(id), data),
    onSuccess: () => {
      invalidateAlunos(id);
      handleSuccess('Aluno atualizado com sucesso!');
      navigate('/alunos');
    },
    onError: (error: Error) => {
      handleError(error, 'Erro ao atualizar aluno');
    },
  });

  const onSubmit = async (data: AlunoFormData) => {
    const payload: any = {
      nome: data.nome,
      rg: data.rg || undefined,
      cpf: data.cpf,
      endereco: data.endereco || undefined,
      bairro: data.bairro || undefined,
      cep: data.cep || undefined,
      cidadeId: data.cidadeId,
      cursoId: data.cursoId,
      serie: data.serie || undefined,
      rm: data.rm || undefined,
      periodo: data.periodo || undefined,
      telefone: data.telefone || undefined,
      telefonePai: data.telefonePai || undefined,
      email: data.email || undefined,
      dataNascimento: data.dataNascimento ? new Date(data.dataNascimento).toISOString() : undefined,
    };

    if (isEditing) {
      await updateMutation.mutateAsync(payload as UpdateAlunoDTO);
    } else {
      await createMutation.mutateAsync(payload as CreateAlunoDTO);
    }
  };

  if (isEditing && loadError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Editar Aluno</h1>
          <p className="mt-1 text-sm text-gray-500">Não foi possível carregar as informações do aluno.</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Erro ao carregar dados</CardTitle>
            <CardDescription>{loadError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/alunos')}>Voltar para lista</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isEditing && isLoadingAluno) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Editar Aluno</h1>
          <p className="mt-1 text-sm text-gray-500">Atualize as informações do aluno</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Dados do Aluno</CardTitle>
          </CardHeader>
          <CardContent>
            <SkeletonForm />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Aluno' : 'Novo Aluno'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing ? 'Atualize as informações do aluno' : 'Preencha os dados do novo aluno'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dados Pessoais</CardTitle>
          <CardDescription>Informações básicas do aluno</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome">Nome Completo *</Label>
                <Input
                  id="nome"
                  {...register('nome')}
                  placeholder="Digite o nome completo"
                  className={errors.nome ? 'border-red-500' : ''}
                />
                {errors.nome && (
                  <p className="mt-1 text-sm text-red-600">{errors.nome.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="rg">RG (com UF opcional)</Label>
                <Input
                  id="rg"
                  {...register('rg')}
                  placeholder="Ex: 123456789/SP"
                  maxLength={20}
                />
              </div>

              <div>
                <Label htmlFor="cpf">CPF *</Label>
                <InputMask
                  id="cpf"
                  mask="cpf"
                  value={cpfValue}
                  onChange={(masked, _raw) => {
                    setCpfValue(masked);
                    // Enviar para o backend com máscara (CHAR(14))
                    setValue('cpf', masked);
                  }}
                  placeholder="000.000.000-00"
                  error={!!errors.cpf}
                />
                {errors.cpf && (
                  <p className="mt-1 text-sm text-red-600">{errors.cpf.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dataNascimento">Data de Nascimento *</Label>
                <Input
                  id="dataNascimento"
                  type="date"
                  {...register('dataNascimento')}
                  className={errors.dataNascimento ? 'border-red-500' : ''}
                />
                {errors.dataNascimento && (
                  <p className="mt-1 text-sm text-red-600">{errors.dataNascimento.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="telefone">Telefone</Label>
                <InputMask
                  id="telefone"
                  mask="phone"
                  value={telefoneValue}
                  onChange={(masked, raw) => {
                    setTelefoneValue(masked);
                    setValue('telefone', raw);
                  }}
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div>
                <Label htmlFor="telefonePai">Telefone Responsável</Label>
                <InputMask
                  id="telefonePai"
                  mask="phone"
                  value={telefonePaiValue}
                  onChange={(masked, raw) => {
                    setTelefonePaiValue(masked);
                    setValue('telefonePai', raw);
                  }}
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="aluno@email.com"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="pt-4 border-t">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Endereço</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="endereco">Endereço</Label>
                  <Input
                    id="endereco"
                    {...register('endereco')}
                    placeholder="Rua, número, complemento"
                  />
                </div>

                <div>
                  <Label htmlFor="bairro">Bairro</Label>
                  <Input
                    id="bairro"
                    {...register('bairro')}
                    placeholder="Digite o bairro"
                  />
                </div>

                <div>
                  <Label htmlFor="cep">CEP</Label>
                  <InputMask
                    id="cep"
                    mask="cep"
                    value={cepValue}
                    onChange={(masked, raw) => {
                      setCepValue(masked);
                      setValue('cep', raw);
                    }}
                    placeholder="00000-000"
                  />
                </div>

                <div>
                  <Label htmlFor="cidadeId">Cidade *</Label>
                  <ServerSearchSelect<Cidade>
                    id="cidadeId"
                    value={watch('cidadeId') || null}
                    onChange={(selectedId) => setValue('cidadeId', selectedId ?? 0, { shouldValidate: true, shouldDirty: true })}
                    fetchPage={(params) => cidadeService.list(params)}
                    fetchById={(selectedId) => cidadeService.getById(selectedId)}
                    getOptionLabel={(cidade) => `${cidade.nome} — ${cidade.uf}`}
                    queryKey="cidades-aluno-select"
                    placeholder="Buscar cidade…"
                    error={Boolean(errors.cidadeId)}
                  />
                  {errors.cidadeId && (
                    <p className="mt-1 text-sm text-red-600">{errors.cidadeId.message}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Dados Acadêmicos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cursoId">Curso *</Label>
                  <ServerSearchSelect<Curso>
                    id="cursoId"
                    value={watch('cursoId') || null}
                    onChange={(selectedId) => setValue('cursoId', selectedId ?? 0, { shouldValidate: true, shouldDirty: true })}
                    fetchPage={(params) => cursoService.list(params)}
                    fetchById={(selectedId) => cursoService.getById(selectedId)}
                    getOptionLabel={(curso) => curso.nome}
                    queryKey="cursos-aluno-select"
                    placeholder="Buscar curso…"
                    error={Boolean(errors.cursoId)}
                  />
                  {errors.cursoId && (
                    <p className="mt-1 text-sm text-red-600">{errors.cursoId.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="rm">RM</Label>
                  <Input
                    id="rm"
                    {...register('rm')}
                    placeholder="Digite o RM"
                    className={errors.rm ? 'border-red-500' : ''}
                  />
                  {errors.rm && (
                    <p className="mt-1 text-sm text-red-600">{errors.rm.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="periodo">Período</Label>
                  <select
                    id="periodo"
                    {...register('periodo')}
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">Selecione o Período</option>
                    <option value="Manhã">Manhã</option>
                    <option value="Tarde">Tarde</option>
                    <option value="Noite">Noite</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="serie">Série</Label>
                  <select
                    id="serie"
                    {...register('serie')}
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">Selecione a série</option>
                    <option value="1ª Série">1ª Série</option>
                    <option value="2ª Série">2ª Série</option>
                    <option value="3ª Série">3ª Série</option>
                    <option value="Módulo I">Módulo I</option>
                    <option value="Módulo II">Módulo II</option>
                    <option value="Módulo III">Módulo III</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/alunos')}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="w-full sm:w-auto"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Salvando...'
                  : isEditing
                    ? 'Atualizar Aluno'
                    : 'Cadastrar Aluno'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};